import { useEffect, useState } from 'react';
import { ApiError, api, type SupportTicket } from '../../api/client';
import { Button, Card, Field, Input, Select, TextArea, useToast } from '../../components/ui';
import { useT } from '../../i18n';
import { formatDate } from '../../lib/format';

const rupees = (paise: number) => `₹${(paise / 100).toLocaleString('en-IN')}`;

/** A parent's invite code: a friend's first report at the referral price (set in the admin console). */
export const InviteCard = () => {
  const t = useT();
  const [invite, setInvite] = useState<{ code: string; price: number; invited: number } | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    void api
      .referral()
      .then((r) => {
        if (r.on && r.code) setInvite({ code: r.code, price: r.price ?? 0, invited: r.invited ?? 0 });
      })
      .catch(() => undefined);
  }, []);

  if (!invite) return null;
  const url = `${window.location.origin}/?code=${encodeURIComponent(invite.code)}&utm_source=invite&utm_medium=referral`;
  const text = t('settings.invite.shareText', { code: invite.code, price: rupees(invite.price), url });
  const share = async () => {
    if (navigator.share) {
      await navigator.share({ text }).catch(() => undefined);
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
    }
  };

  return (
    <Card className="mb-4 space-y-3">
      <h2 className="text-h3 text-ink">{t('settings.invite.title')}</h2>
      <p className="text-body text-ink-muted">{t('settings.invite.body', { price: rupees(invite.price) })}</p>
      <p className="inline-block rounded-md bg-mist px-4 py-2 font-mono text-h3 tracking-wider text-ink">{invite.code}</p>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="secondary"
          onClick={() =>
            void navigator.clipboard?.writeText(invite.code).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            })
          }
        >
          {copied ? t('settings.invite.copied') : t('settings.invite.copy')}
        </Button>
        <Button variant="secondary" onClick={() => void share()}>
          {t('settings.invite.share')}
        </Button>
      </div>
      {invite.invited > 0 && <p className="text-caption text-ink-muted">{t('settings.invite.count', { count: invite.invited })}</p>}
    </Card>
  );
};

const CATEGORIES: Array<SupportTicket['category'] & string> = ['payment', 'report', 'account', 'privacy', 'clinical', 'other'];

/** "Contact support": opens a ticket the team sees in the admin console. */
export const SupportCard = () => {
  const t = useT();
  const toast = useToast();
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [subject, setSubject] = useState('');
  const [text, setText] = useState('');
  const [category, setCategory] = useState<SupportTicket['category'] & string>('other');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const load = () =>
    void api.support
      .list()
      .then((r) => setTickets(r.tickets))
      .catch(() => undefined);
  useEffect(load, []);

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await api.support.create({ subject: subject.trim(), text: text.trim(), category });
      setSubject('');
      setText('');
      toast(t('settings.support.sent'));
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card id="support" className="mb-4 scroll-mt-24 space-y-4">
      <h2 className="text-h3 text-ink">{t('settings.support.title')}</h2>
      <p className="text-body text-ink-muted">{t('settings.support.body')}</p>
      <form onSubmit={send} className="space-y-3">
        <Field id="support-category" label={t('settings.support.category')}>
          <Select value={category} onChange={(e) => setCategory(e.target.value as typeof category)}>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {t(`settings.support.cat.${c}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field id="support-subject" label={t('settings.support.subject')}>
          <Input value={subject} maxLength={140} onChange={(e) => setSubject(e.target.value)} />
        </Field>
        <Field id="support-message" label={t('settings.support.message')} error={error}>
          <TextArea value={text} maxLength={4000} rows={4} onChange={(e) => setText(e.target.value)} />
        </Field>
        <Button type="submit" loading={busy} disabled={subject.trim().length < 3 || text.trim().length < 10}>
          {t('settings.support.send')}
        </Button>
      </form>
      {tickets.length > 0 && (
        <div>
          <p className="mb-2 text-label text-ink">{t('settings.support.yours')}</p>
          <ul className="space-y-2">
            {tickets.map((tk) => {
              const reply = [...tk.thread].reverse().find((m) => m.from === 'staff');
              return (
                <li key={tk.ticketId} className="rounded-md bg-mist px-3 py-2 text-caption text-ink">
                  <p className="flex justify-between gap-2">
                    <span className="font-bold">{tk.subject}</span>
                    <span className="text-ink-muted">
                      {t(`settings.support.status.${tk.status}`)} · {formatDate(tk.createdAt)}
                    </span>
                  </p>
                  {reply && (
                    <p className="mt-1 font-normal text-ink-soft">
                      <span className="font-bold">{t('settings.support.reply')}:</span> {reply.text}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </Card>
  );
};
